import React from 'react';
import type {BoxProps} from '@tempo/core';
import {Box, Heading, Text, VStack} from '@tempo/core';
import AnalyticSkeleton from '@deezer/next/features/Analytic/components/AnalyticSkeleton';
import AnalyticError from '@deezer/next/features/Analytic/components/AnalyticError';
import Placeholder from '@components/Placeholder';
import SectionRow from '@components/PageElements/SectionRow';

type AnalyticCardProps = {
	hasNoData?: boolean;
	isError?: boolean;
	isLoading?: boolean;
	title?: string;
	subtitle?: string;
	placeholder?: React.ReactNode;
	children: React.ReactNode;
	variant?: 'analytic' | 'overview';
	'data-testid'?: string;
} & BoxProps;

export type AnalyticCardState = 'loading' | 'error' | 'empty' | 'ready';

const getAnalyticCardState = ({
	isLoading,
	isError,
	hasNoData,
}: Pick<
	AnalyticCardProps,
	'isLoading' | 'isError' | 'hasNoData'
>): AnalyticCardState => {
	if (isLoading) return 'loading';
	if (isError) return 'error';
	if (hasNoData) return 'empty';
	return 'ready';
};

const AnalyticCard = ({
	children,
	placeholder,
	hasNoData,
	isLoading,
	isError,
	title,
	subtitle,
	variant = 'analytic',
	'data-testid': dataTestId,
	...boxProps
}: AnalyticCardProps) => {
	// Exposed for E2E tests so that loading, error, empty and loaded states can be told apart
	const state = getAnalyticCardState({isLoading, isError, hasNoData});

	if (isLoading) {
		return (
			<AnalyticSkeleton
				variant={variant}
				data-testid={dataTestId}
				data-state={state}
			/>
		);
	}
	const content = isError ? (
		<AnalyticError />
	) : hasNoData ? (
		placeholder || <Placeholder />
	) : (
		children
	);

	if (variant === 'overview') {
		return (
			<Box
				as="section"
				data-testid={dataTestId}
				data-state={state}
				{...boxProps}
			>
				{(title || subtitle) && (
					<SectionRow title={title} subtitle={subtitle} />
				)}
				{content}
			</Box>
		);
	}

	return (
		<VStack
			as="section"
			alignItems="initial"
			py={{base: 'spacing.l', md: 'spacing.xl'}}
			px={{base: 'spacing.l', md: 'spacing.3xl'}}
			width="100%"
			data-testid={dataTestId}
			data-state={state}
			{...boxProps}
			spacing="spacing.s"
		>
			{(title || subtitle) && (
				<VStack spacing="spacing.s" alignItems="initial">
					{title && (
						<Heading as="h2" variant="title.section">
							{title}
						</Heading>
					)}
					{subtitle && (
						<Text
							as="h3"
							variant="body.s"
							color="text.neutral.secondary.default"
						>
							{subtitle}
						</Text>
					)}
				</VStack>
			)}
			<Box flex={1}>{content}</Box>
		</VStack>
	);
};

export default AnalyticCard;
